/* eslint-disable max-lines */
import React, {
 useContext, useRef, useState
} from 'react';
import {
 assistantInstanceService, opcodeAssistantService, assistantProvisioningService
} from 'shared/api';
import type {
 BulkProvisionLog
} from 'shared/api/services/BEX/assistantProvisioning.service';
import {
 useNavigate, useParams
} from 'react-router-dom';
import {
 Button
} from 'shared/components/ui/button';
import {
 Bot, Copy, MoreVertical, Settings, Trash2
} from 'lucide-react';
import {
 Context
} from 'shared/context/GlobalState';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'shared/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu';
import AlertModal from 'shared/components/Alertmodal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'shared/components/ui/dialog';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 formatDateToDDMMYYY
} from 'shared/utils/timeUtils';
import {
 getOrganizationMember
} from 'shared/utils/organizationUtils';
import {
 OrganizationContext
} from 'shared/context/OrganizationContext';
import {
 TextSkeleton
} from 'shared/components/ui/text-skeleton';
import useLazyLoad from 'shared/hooks/useLazyLoading';
import {
 Assistant
} from '../types/builderAssistant.types';

interface AssistantCardProps extends Assistant {
  onManage: () => void;
  setAssistantInstances: React.Dispatch<React.SetStateAction<Assistant[]>>;
}

export default function AssistantCard({
  assistant_id,
  title,
  status,
  created,
  createdBy,
  onManage,
  setAssistantInstances,
}: AssistantCardProps) {
  const [delModal, setDelModal] = useState(false);
  const [delBtn, setDelBtn] = useState(false);
  const [createdByName, setCreatedByName] = useState('');
  const [provisionModal, setProvisionModal] = useState(false);
  const [provisionCount, setProvisionCount] = useState(1);
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [provisionLogs, setProvisionLogs] = useState<string[]>([]);
  const logEndRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const {
 selectedOrganization
} = useContext(OrganizationContext);
  const {
 addNewNotifcation
} = useContext(Context);
  const {
 oragID, subOragID
} = useParams<{
    oragID: string;
    subOragID: string;
  }>();

  const fetchCreatedByInfo = async () => {
    getOrganizationMember(createdBy, selectedOrganization)
      .then((name) => {
        setCreatedByName(name || 'Unknown');
      })
      .catch(() => {

      });
  };

  const {
 elementRef
} = useLazyLoad(fetchCreatedByInfo);

  const handleDeleteAssistant = async () => {
    setDelBtn(true);

    try {
      const delObj = {
        organizationId: oragID,
        subOrganizationId: subOragID,
        assistantId: assistant_id,
      };
      await assistantInstanceService.deleteAssistant(delObj);

      // delete rich assistant in redis (cascade-deletes attached channels)
      const delOpcodeObj = {
        org_id: oragID || '',
        sub_org_id: subOragID || '',
        assistantId: assistant_id,
      };
      await opcodeAssistantService.deleteOpcodeAssistant(delOpcodeObj);

      addNewNotifcation('Assistant deleted successfully', 'success');
      setAssistantInstances((prev) => prev.filter((item) => item.assistant_id !== assistant_id));
    } catch {

      addNewNotifcation('Something went wrong', 'danger');
    } finally {
      setDelBtn(false);
      setDelModal(false);
    }
  };

  const handleProvisionAssistant = async () => {
    if (provisionCount < 1 || provisionCount > 50) {
      addNewNotifcation('Number of assistants must be between 1 and 50', 'warning');
      return;
    }

    setProvisionLoading(true);
    setProvisionLogs([]);

    const addLog = (msg: string) => {
      setProvisionLogs(prev => [...prev, msg]);
      setTimeout(() => logEndRef.current?.scrollIntoView({
 behavior: 'smooth'
}), 50);
    };

    try {
      await assistantProvisioningService.bulkProvisionAssistantStream(
        {
          count: provisionCount,
          template_id: assistant_id,
          template_org_id: oragID!,
          template_sub_org_id: subOragID!,
          target_org_id: oragID!,
          target_sub_org_id: subOragID!,
        },
        (log: BulkProvisionLog) => {
          if (log.type === 'log') {
            addLog(log.message || '');
          } else if (log.type === 'progress') {
            const icon = log.status === 'ok' ? '\u2713' : '\u2717';
            addLog(`${icon} Instance ${log.index}/${log.total} ${log.status === 'ok' ? 'provisioned' : 'failed'}${log.message ? ': ' + log.message : ''}`);
          } else if (log.type === 'done') {
            addLog(log.message || 'Done.');
          }
        }
      );

      // Re-fetch assistant list to show newly provisioned instances
      const res = await opcodeAssistantService.listOpcodeAssistants(oragID, subOragID);
      setAssistantInstances(res.data as unknown as Assistant[]);

      addNewNotifcation(`${provisionCount} assistant(s) provisioned successfully`, 'success');
    } catch {
      addNewNotifcation('Failed to provision assistants', 'danger');
    } finally {
      setProvisionLoading(false);
    }
  };

  const handleStopPropagation = (
    e: React.MouseEvent,
    callback?: () => void
  ) => {
    e.stopPropagation();
    if (callback) callback();
  };

  return (
      <Card
      ref={elementRef}
      className="cursor-pointer"
      onClick={() => navigate(`${assistant_id}`, {
 state: {
 title
}
})}
    >
          <CardHeader>
              <div className="flex w-full justify-between ">
                  <div className="w-full flex flex-col truncate">
                      <CardTitle className="cursor-pointer w-full hover:underline truncate">
                          {title}
                      </CardTitle>
                      {/* Dev-only small mono label showing the auto-generated slug. */}
                      <CardDescription className="font-mono text-xs">{assistant_id}</CardDescription>
                  </div>
                  <DropdownMenu modal={false}>
                      <DropdownMenuTrigger onClick={handleStopPropagation} asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                          </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent onClick={handleStopPropagation} align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={onManage}>
                              <Settings className="mr-2 h-4 w-4" />
                              <span>Manage</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setProvisionModal(true)}>
                              <Copy className="mr-2 h-4 w-4" />
                              <span>Provision</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                onClick={() => setDelModal(true)}
                className="text-red-600"
              >
                              <Trash2 className="mr-2 h-4 w-4" />
                              <span>Delete</span>
                          </DropdownMenuItem>
                      </DropdownMenuContent>
                  </DropdownMenu>
              </div>
          </CardHeader>
          <CardContent>
              <div className="flex gap-3 items-center">
                  <span className="rounded-full size-16 flex justify-center items-center bg-[#b1092a]">
                      <Bot size={28} color="white" />
                  </span>
                  <ul className="flex flex-col">
                      <li className="text-sm text-muted-foreground">
                          Created on: <span>{formatDateToDDMMYYY(created)}</span>{' '}
                      </li>
                      <li className="text-sm text-muted-foreground flex items-center gap-2">
                          Created by:
                          <TextSkeleton
                width={70}
                loading={createdByName === ''}
                className="truncate"
              >
                              <span> {createdByName} </span>
                          </TextSkeleton>
                      </li>
                      <li className="text-sm text-muted-foreground">
                          Status: <span> {status} </span>{' '}
                      </li>
                  </ul>
              </div>
          </CardContent>

          {/* delete collection modal */}
          <AlertModal
        loading={delBtn}
        openModal={delModal}
        setopenModal={setDelModal}
        onDelete={handleDeleteAssistant}
      />

          {/* provision assistant modal */}
          <Dialog open={provisionModal} onOpenChange={(open) => { if (!provisionLoading) { setProvisionModal(open); if (!open) { setProvisionLogs([]); setProvisionCount(1); } } }}>
              <DialogContent onClick={handleStopPropagation}>
                  <DialogHeader>
                      <DialogTitle>Provision Assistant</DialogTitle>
                      <DialogDescription>
                          Choose how many instances to provision from &quot;{title}&quot;. Maximum 50.
                      </DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col gap-2 py-2">
                      <Label htmlFor="provision-count">Number of Assistants</Label>
                      <Input
                id="provision-count"
                type="number"
                min={1}
                max={50}
                value={provisionCount}
                onChange={(e) => setProvisionCount(Math.min(50, Math.max(1, Number(e.target.value))))}
                disabled={provisionLoading}
              />
                  </div>

                  {provisionLogs.length > 0 && (
                      <div className="bg-muted/50 rounded-md border p-3 max-h-48 overflow-y-auto font-mono text-xs space-y-0.5">
                          {provisionLogs.map((log, i) => (
                              <div key={i} className={log.startsWith('\u2717') ? 'text-destructive' : log.startsWith('\u2713') ? 'text-green-600' : 'text-muted-foreground'}>
                                  {log}
                              </div>
                          ))}
                          <div ref={logEndRef} />
                      </div>
                  )}

                  <DialogFooter>
                      {!provisionLoading && provisionLogs.length > 0 ? (
                          <Button onClick={() => { setProvisionModal(false); setProvisionLogs([]); setProvisionCount(1); }}>
                              Close
                          </Button>
                      ) : (
                          <>
                              <Button variant="outline" onClick={() => { setProvisionModal(false); setProvisionLogs([]); setProvisionCount(1); }} disabled={provisionLoading}>
                                  Cancel
                              </Button>
                              <Button onClick={handleProvisionAssistant} disabled={provisionLoading}>
                                  {provisionLoading ? 'Provisioning...' : 'Provision'}
                              </Button>
                          </>
                      )}
                  </DialogFooter>
              </DialogContent>
          </Dialog>
      </Card>
  );
}
